import { throttle } from '@tanstack/react-pacer';
import {
  PartialKeys,
  VirtualItem,
  Virtualizer,
  VirtualizerOptions,
  useWindowVirtualizer,
} from '@tanstack/react-virtual';
import { useInjection } from 'inversify-react';
import { Fragment, useCallback, useEffect, useMemo, useRef } from 'react';

import { VirtualizerService } from '../../../../core/viewer/virtualizer-service';
import { TYPES } from '../../../../inversify.types';
import { StyledViewerWrapper } from './VirtualList.styles';

export type VirtualListProps<T> = {
  className?: string;
  items: T[];
  renderItem: (
    item: T,
    virtualItem: VirtualItem,
    virtualizer?: Virtualizer<Window, Element>
  ) => React.ReactNode;
  virtualizerOptions: PartialKeys<
    VirtualizerOptions<Window, Element>,
    'getScrollElement' | 'observeElementRect' | 'observeElementOffset' | 'scrollToFn'
  >;
  updateActiveItemOnScroll: (activePageNumber: number) => void;
  onWindowResize: () => void;
  zoomLevel: number;
  pageWidth: number;
};

const THROTTLE_RESIZE_WINDOW_IN_MS = 50;
const THROTTLE_SCROLL_IN_MS = 100;

export const VirtualList = <T,>({
  items,
  renderItem,
  virtualizerOptions,
  updateActiveItemOnScroll,
  onWindowResize,
  pageWidth,
  zoomLevel,
}: VirtualListProps<T>) => {
  const ref = useRef<HTMLDivElement>(null);
  const virtualizerService = useInjection<VirtualizerService>(TYPES.Viewer.VirtualizerService);

  const virtualizer = useWindowVirtualizer({
    scrollMargin: ref.current?.offsetTop ?? 0,
    ...virtualizerOptions,
    count: items.length,
  });

  const virtualRows = virtualizer.getVirtualItems();

  const handleScroll = useCallback(() => {
    if (!ref.current) return;

    const { scrollY } = window;
    const viewportHeight = window.innerHeight;
    const activeItem = virtualRows.find((row) => {
      const start = Math.round(row.start);
      const end = Math.round(row.end);
      const scrollMidpoint = scrollY + viewportHeight / 2;

      return scrollMidpoint >= start && scrollMidpoint < end;
    });

    if (activeItem) {
      const activePageNumber = activeItem.index + 1;

      updateActiveItemOnScroll(activePageNumber);
    }
  }, [updateActiveItemOnScroll, virtualRows, virtualizer.options.scrollMargin]);

  const handleThrottledWindowResize = useMemo(
    () =>
      throttle(onWindowResize, {
        wait: THROTTLE_RESIZE_WINDOW_IN_MS,
      }),
    [onWindowResize]
  );

  const throlledHandleScroll = useMemo(
    () => throttle(handleScroll, { wait: THROTTLE_SCROLL_IN_MS }),
    [handleScroll]
  );

  useEffect(() => {
    virtualizerService.setVirtualizer(virtualizer);
  }, [virtualizer, virtualizerService]);

  useEffect(() => {
    //? Initial call to set the active item on mount
    handleScroll();
  }, [handleScroll]);

  useEffect(() => {
    window.addEventListener('scroll', throlledHandleScroll);
    window.addEventListener('touchend', throlledHandleScroll);
    window.addEventListener('resize', handleThrottledWindowResize);

    return () => {
      window.removeEventListener('scroll', throlledHandleScroll);
      window.removeEventListener('touchend', throlledHandleScroll);
      window.removeEventListener('resize', handleThrottledWindowResize);
    };
  }, [handleScroll, handleThrottledWindowResize, throlledHandleScroll]);

  return (
    <div ref={ref}>
      <StyledViewerWrapper
        style={{
          width: `${pageWidth * zoomLevel}px`,
          height: `${virtualizer.getTotalSize()}px`,
        }}
      >
        {virtualRows.map(({ key, index, end, size, start, lane }) => {
          return (
            <Fragment key={key as string}>
              {renderItem(
                items[index],
                {
                  index,
                  size,
                  start,
                  end,
                  key,
                  lane,
                },
                virtualizer
              )}
            </Fragment>
          );
        })}
      </StyledViewerWrapper>
    </div>
  );
};
